import { Alert, Button, Group, Text } from '@mantine/core';
import { IconLayoutGrid } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import useAppEmbedStatus from '../hooks/useAppEmbedStatus';

// An embed that has never been confirmed is only worth flagging once the domain
// has had time to be checked at all; a shop created minutes ago has not.
const UNCONFIRMED_GRACE_MS = 60 * 60 * 1000;

const ShopifyActivationAlert = () => {
  const { domain } = useSelector((state) => state.domain);
  const location = useLocation();
  // The activation guide and the activation step of the wizard own this
  // conversation where they render.
  const onActivationSurface =
    location.pathname.includes('shopifyIntegration') || location.pathname.endsWith('/activate');

  const stored = domain?.appEmbedActive;
  const relevant = domain?.platform === 'shopify' && !onActivationSurface;

  // Checked here, not just displayed here. The stored value only changes when
  // something calls this endpoint or the connector's nightly sweep runs, so an
  // alert that reads it and never looks again keeps telling a merchant their
  // shop is untracked for up to a day after they have fixed it — while showing
  // them the button they already pressed. The re-check on window focus makes it
  // clear itself the moment they come back from the Shopify tab.
  //
  // Only checked when it has something to say: a domain whose embed is already
  // confirmed on costs nothing.
  const { appEmbedActive: live } = useAppEmbedStatus({ domain, enabled: relevant && stored !== true });

  if (!relevant) {
    return null;
  }

  // The live answer wins wherever it exists; the stored value is the fallback
  // for the moment before the first check returns.
  const effective = live ?? stored;
  const confirmedOff = effective === false;
  const createdAt = Date.parse(domain.createdAt);
  const unconfirmed = effective == null && !Number.isNaN(createdAt) && Date.now() - createdAt > UNCONFIRMED_GRACE_MS;

  if (!confirmedOff && !unconfirmed) {
    return null;
  }

  // Only a confirmed-off embed is an assertion that tracking has failed. The
  // unconfirmed case is, by construction, not that — it fires for every
  // Shopify domain until the backfill lands, so it must not carry the same
  // "something is broken" red as the confirmed case.
  // TEMP (client demo): this alert is reframed from tracking to on-site
  // rendering, and de-escalated from red/yellow to yellow/blue. Revert this
  // block, the title, the body and the button label together. The framing is
  // also the more accurate one: with the embed off the web pixel still delivers
  // cart and checkout events, so tracking does not stop - only the SDK-rendered
  // recommenders, banners and stories do.
  const color = confirmedOff ? 'yellow' : 'blue';

  return (
    <Alert
      color={color}
      icon={<IconLayoutGrid size={16} />}
      mb="md"
      title={
        confirmedOff ? "Releva on-site elements aren't showing yet" : "We can't confirm Releva elements are showing"
      }
    >
      <Text size="sm" mb="sm">
        {confirmedOff
          ? 'The Releva.ai app embed is switched off in your Shopify theme, so product recommendations, banners and stories are not being rendered on your storefront.'
          : 'We have not been able to confirm the Releva.ai app embed is enabled in your Shopify theme. If it is off, recommendations, banners and stories will not render on your storefront.'}
      </Text>
      {/* Deliberately no theme-editor deep link here. Opening the editor is only
          half the job - the embed does not take effect until Save is clicked in
          the top-right corner - and an alert has no room to say so without
          becoming a page. Send them to the page that does say it, so nobody
          toggles the embed, navigates away and believes they are done. */}
      <Group gap="sm">
        <Button
          component="a"
          href={`#/accounts/${domain.accountId}/domains/${domain.id}/shopifyIntegration`}
          color={color}
          size="xs"
        >
          Show me how to turn it on
        </Button>
      </Group>
    </Alert>
  );
};

export default ShopifyActivationAlert;
